// M8-ASSESS: a reported round's two views (assessments-screen.tsx hosts them).
// - ReportPanel: the participation banner first when too few answered (the numbers lean on who
//   did), the team tiles (hours against capacity, energy, who is over or under loaded), the
//   action plan's recovered hours, the recommended actions, the gaps, one card per role, and
//   Export HTML (the server's standalone page, saved as a file; it prints to PDF).
// - ProposedChart: what the report suggests for the chart — new seats with the responsibilities
//   and KPIs that hang on them, then additions to existing seats — each accepted or rejected by
//   its own hover icon. "Review changes" asks the server what applying would create, reuse or
//   skip and why (previewApply); Apply writes it only on that confirmation.
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Notice,
  StatGrid,
  StatTile,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckIcon,
  DownloadIcon,
  GitCompareArrowsIcon,
  NetworkIcon,
  RotateCcwIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { Loading } from "../screen-parts";
import { ErrorNotice, FormError } from "../structure/parts";
import {
  DIFF_KIND_LABEL,
  type DiffLine,
  diffSummary,
  energyWord,
  FIT_LABEL,
  groupProposals,
  HEALTH_LABEL,
  HEALTH_TONE,
  hours,
  LOAD_LABEL,
  loadPercent,
  type Proposal,
  type ReportBody,
  type RoundDetail,
  SEVERITY_TONE,
} from "./assess-model";
import { assessKeys, reportQuery } from "./assess-queries";

const CARD = "rounded-lg border border-border bg-card p-4";

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// Report ------------------------------------------------------------------------------------

export function ReportPanel({
  api,
  company,
  round,
}: {
  api: Api;
  company: CompanyAccess;
  round: RoundDetail;
}) {
  const report = useQuery(reportQuery(api, company.id, round.id));
  const exportHtml = useMutation({
    mutationFn: () =>
      api.client.assessments.reportHtml({ params: { co: company.id, id: round.id } }),
    onSuccess: (html) => {
      const url = URL.createObjectURL(new Blob([String(html)], { type: "text/html" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${round.name.replace(/[^\w-]+/g, "-").toLowerCase()}-report.html`;
      a.click();
      URL.revokeObjectURL(url);
      toast.add({ title: "Report exported", description: "Open it and print to PDF if needed." });
    },
  });
  if (report.isPending) return <Loading label="Loading the report" rows={6} />;
  if (report.isError) return <ErrorNotice what="The report" error={report.error} />;
  const b = report.data.body;
  return (
    <div className="flex flex-col gap-5" data-slot="assessment-report">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Version {report.data.version} · computed {formatDateTime(report.data.generated_at)} ·{" "}
          {b.participation.completed} of {b.participation.invited} answered
        </p>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => exportHtml.mutate()}
          disabled={exportHtml.isPending}
        >
          <DownloadIcon aria-hidden={true} />
          Export HTML
        </Button>
      </div>
      <FormError what="The export" error={exportHtml.error} />
      {b.participation.low ? (
        <Notice tone="warning" title="Few people answered" data-slot="participation-banner">
          Only {Math.round(b.participation.rate * 100)}% of the people asked took part; read the
          numbers as a partial picture.
          {b.participation.non_participants.length
            ? ` Missing: ${b.participation.non_participants.map((p) => p.name).join(", ")}.`
            : ""}
        </Notice>
      ) : null}
      <TeamTiles b={b} />
      <section className="flex flex-col gap-3" aria-labelledby="assess-actions">
        <h2 id="assess-actions" className="text-base font-semibold text-foreground">
          Recommended actions
        </h2>
        <StatGrid dense>
          <StatTile
            size="sm"
            label="Hours recovered"
            value={hours(b.action_plan.hours_recovered)}
            detail="per week, if the plan is followed"
          />
          <StatTile
            size="sm"
            label="Workdays"
            value={Math.round(b.action_plan.workdays_recovered * 10) / 10}
            detail="recovered per week"
          />
          <StatTile size="sm" label="Tasks handed off" value={b.action_plan.tasks_handed_off} />
        </StatGrid>
        {b.actions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing to act on — the team is in balance.
          </p>
        ) : (
          <ol className="flex flex-col gap-2">
            {b.actions.map((a, i) => (
              <li key={a.key} className={`${CARD} flex gap-3`} data-action={a.kind}>
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary-text">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-baseline gap-x-2 font-medium text-foreground">
                    {a.title}
                    {a.hours_per_week ? (
                      <span className="text-xs font-normal text-muted-foreground tabular-nums">
                        {hours(a.hours_per_week)} / week
                      </span>
                    ) : null}
                  </p>
                  <p className="text-sm text-muted-foreground">{a.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
      {b.gaps.length ? (
        <section className="flex flex-col gap-3" aria-labelledby="assess-gaps">
          <h2 id="assess-gaps" className="text-base font-semibold text-foreground">
            Gaps
          </h2>
          <ul className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
            {b.gaps.map((g) => (
              <li key={`${g.kind}:${g.title}`} className={CARD} data-gap={g.kind}>
                <p className="flex items-start justify-between gap-2 font-medium text-foreground">
                  <span className="min-w-0">{g.title}</span>
                  <Badge variant={SEVERITY_TONE[g.severity] ?? "neutral"}>{g.severity}</Badge>
                </p>
                <p className="mt-1 text-sm text-muted-foreground">{g.detail}</p>
                {g.people.length ? (
                  <p className="mt-2 text-xs text-muted-foreground">{g.people.join(" · ")}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section className="flex flex-col gap-3" aria-labelledby="assess-roles">
        <h2 id="assess-roles" className="text-base font-semibold text-foreground">
          Roles
        </h2>
        <ul className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
          {b.roles.map((r) => (
            <RoleCard key={r.participant_id} r={r} />
          ))}
        </ul>
      </section>
    </div>
  );
}

function TeamTiles({ b }: { b: ReportBody }) {
  const t = b.team;
  return (
    <StatGrid dense data-slot="team-tiles">
      <StatTile
        size="sm"
        label="Hours vs capacity"
        value={`${Math.round(t.claimed_hours)} / ${t.capacity_hours === null ? "—" : Math.round(t.capacity_hours)} h`}
        detail="a week, claimed vs. available"
      />
      <StatTile
        size="sm"
        label="Energy"
        value={energyWord(t.energy_score)}
        detail={`${hours(t.draining_hours)} draining of ${hours(t.claimed_hours)}`}
      />
      <StatTile
        size="sm"
        label="Overloaded"
        value={t.overloaded}
        detail={t.overloaded === 1 ? "person over capacity" : "people over capacity"}
      />
      <StatTile
        size="sm"
        label="Has room"
        value={t.underused}
        detail={t.underused === 1 ? "person under capacity" : "people under capacity"}
      />
    </StatGrid>
  );
}

function RoleCard({ r }: { r: ReportBody["roles"][number] }) {
  return (
    <li className={`${CARD} flex flex-col gap-3`} data-role-card={r.participant_id}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{r.name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {r.role_title ?? "No role named"}
          </p>
        </div>
        <Badge variant={HEALTH_TONE[r.health] ?? "neutral"} dot>
          {HEALTH_LABEL[r.health]}
        </Badge>
      </div>
      {r.participated ? (
        <>
          <dl className="grid grid-cols-4 gap-2 text-sm max-md:grid-cols-2">
            <div>
              <dt className="text-xs text-muted-foreground">Load</dt>
              <dd className="font-medium tabular-nums">{loadPercent(r.load_index)}</dd>
              <dd className="text-xs text-muted-foreground">{LOAD_LABEL[r.load_flag]}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Energy</dt>
              <dd className="font-medium">{energyWord(r.energy_score)}</dd>
              <dd className="text-xs text-muted-foreground tabular-nums">
                {Math.round(r.draining_share * 100)}% draining
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Fit</dt>
              <dd className="font-medium">{FIT_LABEL[r.fit_risk]}</dd>
              <dd className="text-xs text-muted-foreground tabular-nums">
                {hours(r.misaligned_hours)} off-skill
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Clarity</dt>
              <dd className="font-medium tabular-nums">
                {r.clarity_score === null ? "—" : `${Math.round(r.clarity_score * 10) / 10} / 5`}
              </dd>
              <dd className="text-xs text-muted-foreground tabular-nums">
                {hours(r.claimed_hours)} / {hours(r.capacity_hours)}
              </dd>
            </div>
          </dl>
          {r.top_tasks.length ? (
            <p className="text-xs text-muted-foreground">
              Most time: {r.top_tasks.map((x) => `${x.name} (${hours(x.hours)})`).join(" · ")}
            </p>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Didn't answer; not in the numbers.</p>
      )}
    </li>
  );
}

// Proposed chart ----------------------------------------------------------------------------

const PROPOSAL_TONE = {
  proposed: "neutral",
  accepted: "info",
  rejected: "outline",
  applied: "success",
} as const;
const PROPOSAL_LABEL = {
  proposed: "Proposed",
  accepted: "Accepted",
  rejected: "Rejected",
  applied: "Applied",
} as const;
const KIND_LABEL = { seat: "Seat", responsibility: "Responsibility", metric: "KPI" } as const;

export function ProposedChart({
  api,
  company,
  round,
}: {
  api: Api;
  company: CompanyAccess;
  round: RoundDetail;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const report = useQuery(reportQuery(api, co, round.id));
  const [reviewing, setReviewing] = useState(false);
  const decide = useMutation({
    mutationFn: (v: { p: Proposal; state: "proposed" | "accepted" | "rejected" }) =>
      api.client.assessments.decideProposal({
        params: { co, id: v.p.id },
        body: { state: v.state },
      }),
    // A decision can unblock what hangs on a seat: read the whole list again.
    onSuccess: () => qc.invalidateQueries({ queryKey: assessKeys.report(co, round.id) }),
  });
  if (report.isPending) return <Loading label="Loading the proposals" rows={6} />;
  if (report.isError) return <ErrorNotice what="The proposals" error={report.error} />;
  const proposals = report.data.proposals;
  if (proposals.length === 0)
    return (
      <EmptyState
        variant="card"
        icon={NetworkIcon}
        title="No chart changes proposed"
        description="The answers fit the chart as it is."
      />
    );
  const groups = groupProposals(proposals);
  const accepted = proposals.filter((p) => p.state === "accepted").length;
  const applied = proposals.filter((p) => p.state === "applied").length;
  return (
    <div className="flex flex-col gap-4" data-slot="proposed-chart">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {proposals.length} suggestions · {accepted} accepted
          {applied ? ` · ${applied} applied` : ""}. Accept what you want on the chart, then review
          the changes.
        </p>
        <Button size="sm" disabled={accepted === 0} onClick={() => setReviewing(true)}>
          <GitCompareArrowsIcon aria-hidden={true} />
          Review changes{accepted ? ` (${accepted})` : ""}
        </Button>
      </div>
      <FormError what="The decision" error={decide.error} />
      <ul className="flex flex-col gap-3" aria-label="Proposed chart">
        {groups.map((g) => (
          <li key={g.key} className={`${CARD} flex flex-col gap-2 p-3`} data-group={g.key}>
            {g.seat ? (
              <ProposalRow p={g.seat} name={g.title} lead under={g.under} decide={decide} />
            ) : (
              <p className="px-2 py-1">
                <span className="font-medium text-foreground">{g.title}</span>
                <span className="ml-2 text-xs text-muted-foreground">{g.under}</span>
              </p>
            )}
            {g.items.length ? (
              <ul className="ml-4 flex flex-col gap-1 border-l border-border pl-3 max-md:ml-1 max-md:pl-2">
                {g.items.map((p) => (
                  <ProposalRow key={p.id} p={p} decide={decide} />
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
      {reviewing ? (
        <DiffDialog api={api} company={company} round={round} onClose={() => setReviewing(false)} />
      ) : null}
    </div>
  );
}

type Decide = ReturnType<
  typeof useMutation<
    unknown,
    Error,
    { p: Proposal; state: "proposed" | "accepted" | "rejected" },
    unknown
  >
>;

function ProposalRow({
  p,
  name = p.title,
  lead = false,
  under,
  decide,
}: {
  p: Proposal;
  /** What the row is called (a seat's own title, not "New seat: …"). */
  name?: string;
  lead?: boolean;
  under?: string | null;
  decide: Decide;
}) {
  const Tag = lead ? "div" : "li";
  const pending = decide.isPending && decide.variables?.p.id === p.id;
  const done = p.state === "applied";
  return (
    <Tag
      className="group flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-muted/60"
      data-proposal={p.key}
      data-state={p.state}
    >
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-xs text-muted-foreground">{KIND_LABEL[p.kind]}</span>
          <span
            className={`font-medium text-foreground ${p.state === "rejected" ? "line-through opacity-60" : ""} ${lead ? "text-base" : "text-sm"}`}
          >
            {name}
          </span>
          {p.state !== "proposed" ? (
            <Badge variant={PROPOSAL_TONE[p.state]}>{PROPOSAL_LABEL[p.state]}</Badge>
          ) : null}
          {p.blocked && p.state === "proposed" ? (
            <span className="text-xs text-warning-text">Needs its seat accepted</span>
          ) : null}
        </p>
        {under ? <p className="text-xs text-muted-foreground">{under}</p> : null}
        {p.detail ? <p className="text-xs text-muted-foreground">{p.detail}</p> : null}
      </div>
      {done ? null : (
        <div className="flex shrink-0 items-center gap-0.5 opacity-60 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 max-md:opacity-100">
          {p.state === "proposed" ? (
            <>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label={`Accept ${name}`}
                title={p.blocked ? "Accept its seat first" : "Accept"}
                disabled={pending || p.blocked}
                onClick={() => decide.mutate({ p, state: "accepted" })}
              >
                <CheckIcon aria-hidden={true} />
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label={`Reject ${name}`}
                title="Reject"
                disabled={pending}
                onClick={() => decide.mutate({ p, state: "rejected" })}
              >
                <XIcon aria-hidden={true} />
              </Button>
            </>
          ) : (
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label={`Undo ${name}`}
              title="Back to proposed"
              disabled={pending}
              onClick={() => decide.mutate({ p, state: "proposed" })}
            >
              <RotateCcwIcon aria-hidden={true} />
            </Button>
          )}
        </div>
      )}
    </Tag>
  );
}

const ACTION_TONE = { create: "success", reuse: "info", skip: "warning" } as const;
const ACTION_LABEL = { create: "Create", reuse: "Reuse", skip: "Skip" } as const;

function DiffDialog({
  api,
  company,
  round,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  round: RoundDetail;
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const preview = useQuery({
    queryKey: [...assessKeys.report(co, round.id), "preview"],
    queryFn: () => api.client.assessments.previewApply({ params: { co, id: round.id }, body: {} }),
    gcTime: 0,
  });
  const apply = useMutation({
    mutationFn: () => api.client.assessments.apply({ params: { co, id: round.id }, body: {} }),
    onSuccess: (res) => {
      // Seats, responsibilities, roles and KPIs were written: the whole company's reads move.
      void qc.invalidateQueries({ queryKey: ["company", co] });
      const c = res.created;
      toast.add({
        title: "Applied to the chart",
        description: `Created ${count(c.seats.length, "seat")}, ${count(c.responsibilities.length, "responsibility", "responsibilities")} and ${count(c.metrics.length, "KPI")}.`,
        type: "success",
      });
      onClose();
    },
  });
  const lines = preview.data?.lines ?? [];
  const writes = preview.data ? preview.data.counts.create + preview.data.counts.reuse : 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-2xl" data-slot="apply-diff">
        <DialogHeader>
          <DialogTitle>Review changes to the chart</DialogTitle>
          <DialogDescription>
            {preview.data
              ? diffSummary(preview.data.counts)
              : "What applying the accepted suggestions would write."}
          </DialogDescription>
        </DialogHeader>
        {preview.isPending ? (
          <Loading label="Computing the changes" rows={4} />
        ) : preview.isError ? (
          <ErrorNotice what="The changes" error={preview.error} />
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
            {lines.map((l: DiffLine, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: one proposal can give several lines
                key={`${l.proposal_id}:${l.kind}:${i}`}
                className="flex items-start gap-3 px-3 py-2 text-sm"
                data-diff={l.action}
              >
                <Badge variant={ACTION_TONE[l.action]} className="mt-0.5 w-14 justify-center">
                  {ACTION_LABEL[l.action]}
                </Badge>
                <div className="min-w-0 flex-1">
                  <p className="text-foreground">
                    <span className="text-xs text-muted-foreground">
                      {DIFF_KIND_LABEL[l.kind]}{" "}
                    </span>
                    <span className="font-medium">{l.name}</span>
                  </p>
                  {l.parent ? <p className="text-xs text-muted-foreground">on {l.parent}</p> : null}
                  {l.reason ? <p className="text-xs text-muted-foreground">{l.reason}</p> : null}
                </div>
              </li>
            ))}
          </ul>
        )}
        <FormError what="Applying" error={apply.error} />
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!preview.data || writes === 0 || apply.isPending}
            onClick={() => apply.mutate()}
          >
            {apply.isPending ? "Applying…" : "Apply to the chart"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
